import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import {
  Plus, Trash2, Edit2, AlertTriangle, ChevronLeft, ChevronRight, X,
  ListChecks, CalendarRange, Users, Gauge, Hammer, Search, GripVertical, Printer, ChevronUp, ChevronDown
} from 'lucide-react';
import { DatePickerField } from './DatePickerField';
import { printFabSchedule } from './fabSchedulePrint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/fab-scheduler`;

const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const parseD = (s) => (s ? new Date(s + 'T00:00:00') : null);
const fmtShort = (s) => { const d = parseD(s); return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''; };
const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };

const STATUS_COLORS = {
  quoted: 'bg-slate-100 text-slate-700', awarded: 'bg-indigo-100 text-indigo-700',
  waiting_for_drawings: 'bg-amber-100 text-amber-800', waiting_for_material: 'bg-amber-100 text-amber-800',
  ready_for_scheduling: 'bg-cyan-100 text-cyan-800', scheduled: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-emerald-100 text-emerald-800', on_hold: 'bg-red-100 text-red-800',
  ready_for_inspection: 'bg-violet-100 text-violet-800', ready_for_shipping: 'bg-teal-100 text-teal-800',
  completed: 'bg-green-600 text-white',
};
const PRIORITY_COLORS = {
  emergency: 'bg-red-600 text-white', rush: 'bg-orange-600 text-white', high: 'bg-amber-400 text-amber-950',
  standard: 'bg-slate-300 text-slate-800', low: 'bg-slate-200 text-slate-600',
};
const PRIORITY_BAR = {
  emergency: 'bg-red-500', rush: 'bg-orange-400', high: 'bg-amber-400',
  standard: 'bg-blue-400', low: 'bg-slate-400',
};
const READINESS = {
  not_ready: { label: 'Not ready', cls: 'text-red-600' },
  ordered: { label: 'Ordered', cls: 'text-amber-700' },
  in_progress: { label: 'In progress', cls: 'text-amber-700' },
  ready: { label: 'Ready', cls: 'text-emerald-600' },
  none: { label: '—', cls: 'text-slate-500' },
  out: { label: 'Out', cls: 'text-amber-700' },
  received: { label: 'Received', cls: 'text-emerald-600' },
};

const StatusBadge = ({ status, meta }) => {
  const label = meta?.statuses?.find(s => s.value === status)?.label || status;
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-700'}`}>{label}</span>;
};
const PriorityBadge = ({ priority }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${PRIORITY_COLORS[priority] || 'bg-slate-200'}`}>{priority}</span>
);

const ModalField = ({ label, children }) => (
  <div><label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>{children}</div>
);
const MODAL_INPUT_CLS = "w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-blue-500";

// ─────────────────────────────────────────────────────────────────────
export default function FabScheduler() {
  const { user } = useAuth();
  const [tab, setTab] = useState('list');
  const [meta, setMeta] = useState(null);
  const [winStart, setWinStart] = useState(() => localDate(mondayOf(new Date())));
  const winEnd = (() => { const d = parseD(winStart); d.setDate(d.getDate() + 20); return localDate(d); })();

  useEffect(() => {
    axios.get(`${API}/meta`).then(r => setMeta(r.data)).catch(() => {});
  }, []);

  const canEdit = meta?.can_edit;
  const shiftWeek = (dir) => { const d = parseD(winStart); d.setDate(d.getDate() + dir * 7); setWinStart(localDate(d)); };

  const tabs = [
    { key: 'list', label: 'Project List', icon: ListChecks },
    { key: 'gantt', label: 'Weekly Schedule', icon: CalendarRange },
    { key: 'employees', label: 'Employee Schedule', icon: Users },
    { key: 'capacity', label: 'Capacity', icon: Gauge },
  ];

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="fab-scheduler-page">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-3">
            <Hammer className="w-7 h-7 text-slate-900" />
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Fabrication Scheduler</h1>
          </div>
          <p className="text-blue-800 text-sm">Production plan, manpower capacity and job progress for the fab shop</p>
        </div>
        <button onClick={() => printFabSchedule({ winStart, winEnd, user, meta })} data-testid="fab-print-btn"
          title="Print the priority list, backlog and the weekly schedule for the selected window"
          className="flex items-center gap-2 px-4 py-2 bg-white text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-semibold shadow">
          <Printer className="w-4 h-4" /> Print schedule
        </button>
      </div>
      <div className="mb-4" />

      <div className="flex flex-wrap gap-1 mb-4 bg-slate-900/5 backdrop-blur rounded-xl p-1 w-fit">
        {tabs.map(t => (
          <button key={t.key} data-testid={`fab-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-white text-blue-700 shadow' : 'text-slate-900 hover:bg-slate-900/5'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {(tab === 'gantt' || tab === 'employees' || tab === 'capacity') && (
        <div className="flex items-center gap-3 mb-3 text-slate-900">
          <button onClick={() => shiftWeek(-1)} data-testid="fab-week-prev" className="p-1.5 bg-slate-900/5 hover:bg-slate-900/10 rounded"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-medium">{fmtShort(winStart)} – {fmtShort(winEnd)}</span>
          <button onClick={() => shiftWeek(1)} data-testid="fab-week-next" className="p-1.5 bg-slate-900/5 hover:bg-slate-900/10 rounded"><ChevronRight className="w-4 h-4" /></button>
          <button onClick={() => setWinStart(localDate(mondayOf(new Date())))} className="text-xs px-2 py-1 bg-slate-900/5 hover:bg-slate-900/10 rounded">This week</button>
        </div>
      )}

      {tab === 'list' && <ProjectListTab meta={meta} canEdit={canEdit} />}
      {tab === 'gantt' && <GanttTab winStart={winStart} winEnd={winEnd} meta={meta} />}
      {tab === 'employees' && <EmployeeTab winStart={winStart} winEnd={winEnd} canEdit={canEdit} />}
      {tab === 'capacity' && <CapacityTab winStart={winStart} winEnd={winEnd} />}
    </div>
  );
}

// ── Project List ─────────────────────────────────────────────────────
const fmtH = (n) => (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

function BacklogFooter({ rows, colSpan }) {
  const open = rows.filter(p => p.status !== 'completed');
  const est = open.reduce((s, p) => s + (Number(p.estimated_hours) || 0), 0);
  const act = open.reduce((s, p) => s + (Number(p.actual_hours) || 0), 0);
  const rem = open.reduce((s, p) => s + (Number(p.remaining_hours) || 0), 0);
  return (
    <tfoot>
      <tr className="bg-slate-100 border-t-2 border-slate-300 text-slate-800 font-semibold" data-testid="fab-backlog-footer">
        <td colSpan={colSpan} className="px-2 py-2 text-right">
          <span className="text-slate-500 font-normal">{open.length} open job{open.length !== 1 ? 's' : ''} · completed jobs excluded</span>
          <span className="ml-4">Est <span data-testid="fab-total-est">{fmtH(est)}</span> h</span>
          <span className="ml-4">Actual <span data-testid="fab-total-actual">{fmtH(act)}</span> h</span>
          <span className="ml-4 px-2 py-0.5 rounded bg-blue-600 text-white">Backlog <span data-testid="fab-total-backlog">{fmtH(rem)}</span> h remaining</span>
        </td>
      </tr>
    </tfoot>
  );
}

function ProjectListTab({ meta, canEdit }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // project or {} for new
  const [q, setQ] = useState('');
  const [dragId, setDragId] = useState(null);
  const dragRef = useRef(null);
  const [overId, setOverId] = useState(null);

  const [shopSync, setShopSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try { const r = await axios.get(`${API}/projects`); setProjects(r.data.projects || []); setShopSync(r.data.shop_sync || null); }
    catch { if (!quiet) toast.error('Failed to load projects'); }
    if (!quiet) setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  // Live: pull from the shop timesheets every 2 minutes and whenever the tab regains focus
  useEffect(() => {
    const t = setInterval(() => load(true), 120000);
    const onFocus = () => load(true);
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);
  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/projects/sync-shop`);
      const added = r.data.added || [];
      toast.success(added.length ? `Synced — ${added.length} job${added.length !== 1 ? 's' : ''} added from shop timesheets: ${added.join(', ')}` : `Synced with shop timesheets${r.data.status_changes ? ` — ${r.data.status_changes} status change${r.data.status_changes !== 1 ? 's' : ''}` : ''}`);
      await load(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    setSyncing(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete project ${p.work_order_number || p.project_name}? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/projects/${p.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const canDrag = canEdit && !q;
  const persistOrder = async (ids) => {
    const byId = Object.fromEntries(projects.map(p => [p.id, p]));
    setProjects(ids.map((id, i) => ({ ...byId[id], sequence_number: i + 1 })));
    try { await axios.put(`${API}/projects/reorder`, { ordered_ids: ids }); toast.success('Priority order saved'); }
    catch { toast.error('Could not save order'); load(); }
  };
  const dropOn = (targetId) => {
    const from = dragRef.current;
    dragRef.current = null; setDragId(null); setOverId(null);
    if (!from || from === targetId) return;
    const ids = projects.map(p => p.id);
    const fi = ids.indexOf(from), ti = ids.indexOf(targetId);
    if (fi < 0 || ti < 0) return;
    ids.splice(fi, 1); ids.splice(ti, 0, from);
    persistOrder(ids);
  };
  const moveBy = (id, dir) => {
    const ids = projects.map(p => p.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    persistOrder(ids);
  };

  const filtered = projects.filter(p => {
    if (!q) return true;
    const s = `${p.work_order_number} ${p.customer_name} ${p.project_name} ${p.project_manager}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });
  const HEADERS = ['#', 'WO', 'Customer', 'Project', 'Priority', 'Status', 'Est Hrs', 'Rem Hrs', '% ', 'Planned Start', 'Planned Comp', 'Required', 'Worked by (shop)', 'Work Area', 'Matl', 'Dwg', '!', ''];

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search WO, customer, project, PM"
            data-testid="fab-search" className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500" />
        </div>
        {canEdit && <span className="text-xs text-slate-500 hidden md:inline">{q ? 'Clear the search to reorder' : 'Drag rows by the ⋮⋮ handle (or use ▲▼) to set priority order'}</span>}
        <button onClick={syncNow} disabled={syncing} data-testid="fab-sync-shop-btn"
          title="Jobs, hours, status and crew come from the shop timesheets automatically (every 2 min and whenever a shop timesheet is saved). Click to pull now."
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-300 hover:bg-emerald-100 disabled:opacity-60">
          <span className={syncing ? 'animate-spin' : ''}>⟳</span> {syncing ? 'Syncing…' : 'Shop timesheets'}{shopSync?.at && <span className="text-emerald-700/80 font-normal hidden sm:inline">· {new Date(shopSync.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
        </button>
        {canEdit && (
          <button onClick={() => setEditing({})} data-testid="fab-new-project-btn"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> New Project
          </button>
        )}
      </div>

      {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> :
        filtered.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="fab-empty">No fabrication projects yet.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  {HEADERS.map((h, i) => (
                    <th key={i} className="px-2 py-2 text-left font-semibold border-b border-slate-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => (
                  <tr key={p.id} data-testid={`fab-row-${p.id}`}
                    draggable={canDrag}
                    onDragStart={e => { dragRef.current = p.id; setDragId(p.id); e.dataTransfer.effectAllowed = 'move'; }}
                    onDragOver={e => { if (canDrag && dragRef.current) { e.preventDefault(); if (overId !== p.id) setOverId(p.id); } }}
                    onDrop={e => { e.preventDefault(); dropOn(p.id); }}
                    onDragEnd={() => { dragRef.current = null; setDragId(null); setOverId(null); }}
                    className={`${i % 2 ? 'bg-slate-50/50' : ''} hover:bg-blue-50 cursor-pointer ${dragId === p.id ? 'opacity-40' : ''} ${overId === p.id && dragId && dragId !== p.id ? 'outline outline-2 outline-blue-500 -outline-offset-2' : ''}`}
                    onClick={() => setEditing(p)}>
                    <td className="px-1 py-1 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      <span className="inline-flex items-center gap-1">
                        <span className={`inline-flex items-center justify-center gap-0.5 min-w-[44px] h-9 px-1 rounded-md ${canDrag ? 'cursor-grab text-slate-500 hover:text-blue-700 hover:bg-blue-50 active:cursor-grabbing' : 'text-slate-700'}`} data-testid={`fab-drag-${p.id}`} title={canDrag ? 'Drag to reorder' : ''}>
                          <GripVertical className="w-5 h-5" /><span className="font-mono text-sm font-semibold text-slate-600">{p.sequence_number ?? '–'}</span>
                        </span>
                        {canDrag && (
                          <span className="inline-flex flex-col gap-0.5">
                            <button onClick={() => moveBy(p.id, -1)} disabled={i === 0} data-testid={`fab-move-up-${p.id}`} title="Move up"
                              className="w-9 h-7 flex items-center justify-center rounded-md bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700 active:bg-blue-200 disabled:opacity-25 disabled:hover:bg-slate-100">
                              <ChevronUp className="w-5 h-5" />
                            </button>
                            <button onClick={() => moveBy(p.id, 1)} disabled={i === filtered.length - 1} data-testid={`fab-move-down-${p.id}`} title="Move down"
                              className="w-9 h-7 flex items-center justify-center rounded-md bg-slate-100 text-slate-600 hover:bg-blue-100 hover:text-blue-700 active:bg-blue-200 disabled:opacity-25 disabled:hover:bg-slate-100">
                              <ChevronDown className="w-5 h-5" />
                            </button>
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-2 py-2 font-mono font-bold text-blue-700 whitespace-nowrap">{p.work_order_number || '—'}{p.auto_added && <span className="ml-1 text-[9px] font-sans font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-300 rounded px-1 py-px" title={`Added automatically — the shop logged hours on this WO (first ${p.first_worked_date || '—'})`} data-testid={`fab-auto-${p.id}`}>auto</span>}</td>
                    <td className="px-2 py-2 text-slate-800 whitespace-nowrap">{p.customer_name || '—'}</td>
                    <td className="px-2 py-2 text-slate-800 max-w-[160px] truncate">{p.project_name || '—'}</td>
                    <td className="px-2 py-2"><PriorityBadge priority={p.priority} /></td>
                    <td className="px-2 py-2"><StatusBadge status={p.status} meta={meta} /></td>
                    <td className="px-2 py-2 text-slate-800">{p.estimated_hours || '—'}</td>
                    <td className="px-2 py-2 text-slate-800">{p.remaining_hours}</td>
                    <td className="px-2 py-2">
                      <div className="w-12 bg-slate-200 rounded-full h-2"><div className="bg-emerald-500 h-2 rounded-full" style={{ width: `${p.percent_complete}%` }} /></div>
                      <span className="text-[10px] text-slate-500">{p.percent_complete}%</span>
                    </td>
                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{fmtShort(p.planned_start_date) || '—'}</td>
                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{fmtShort(p.planned_completion_date) || '—'}</td>
                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{fmtShort(p.required_completion_date) || '—'}</td>
                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap max-w-[190px] truncate" title={(p.worked_by || []).map(w => `${w.name} ${w.hours}h`).join('\n') + (p.last_worked_date ? `\nlast worked ${p.last_worked_date}` : '')} data-testid={`fab-worked-${p.id}`}>
                      {(p.worked_by || []).length ? (
                        <>{p.worked_by.slice(0, 2).map(w => w.name.split(' ')[0]).join(', ')}{p.worked_by.length > 2 ? ` +${p.worked_by.length - 2}` : ''} <span className="text-slate-500">· {p.shop_hours}h{p.last_worked_date ? ` · ${fmtShort(p.last_worked_date)}` : ''}</span></>
                      ) : <span className="text-slate-500">{(p.assigned_employee_ids || []).length ? `${p.assigned_employee_ids.length} assigned` : '—'}</span>}
                    </td>
                    <td className="px-2 py-2 text-slate-700 whitespace-nowrap max-w-[110px] truncate">{p.work_area || '—'}</td>
                    <td className={`px-2 py-2 ${READINESS[p.material_status]?.cls || ''}`}>{READINESS[p.material_status]?.label || p.material_status}</td>
                    <td className={`px-2 py-2 ${READINESS[p.drawing_status]?.cls || ''}`}>{READINESS[p.drawing_status]?.label || p.drawing_status}</td>
                    <td className="px-2 py-2 text-center">
                      {(p.warnings || []).length > 0 && (
                        <span title={p.warnings.join('\n')} data-testid={`fab-warn-${p.id}`}>
                          <AlertTriangle className="w-4 h-4 text-amber-600 inline" />
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                      {canEdit && <button onClick={() => remove(p)} data-testid={`fab-delete-${p.id}`} className="text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <BacklogFooter rows={filtered} colSpan={HEADERS.length} />
            </table>
          </div>
        )}
      {editing && <ProjectModal project={editing} meta={meta} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// ── Project create/edit modal ────────────────────────────────────────
function ProjectModal({ project, meta, onClose, onSaved }) {
  const isNew = !project.id;
  const [form, setForm] = useState(() => ({
    work_order_number: '', customer_name: '', project_name: '', project_manager: '',
    date_received: '', required_completion_date: '', estimated_hours: '',
    actual_hours_override: '', status: 'quoted', priority: 'standard',
    material_status: 'not_ready', drawing_status: 'not_ready', subcontract_status: 'none',
    work_area: '', sequence_number: '', percent_complete_override: '',
    planned_start_date: '', planned_completion_date: '', notes: '',
    assigned_employee_ids: [], change_reason: '',
    ...project,
  }));
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/employees`).then(r => setEmployees(r.data.employees || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleEmp = (id) => setForm(f => ({
    ...f, assigned_employee_ids: (f.assigned_employee_ids || []).includes(id)
      ? f.assigned_employee_ids.filter(x => x !== id) : [...(f.assigned_employee_ids || []), id]
  }));

  const woLookup = async () => {
    if (!form.work_order_number) return;
    try {
      const r = await axios.get(`${API}/wo-lookup`, { params: { wo: form.work_order_number } });
      if (r.data.matched) {
        setForm(f => ({
          ...f,
          customer_name: f.customer_name || r.data.customer_name,
          project_name: f.project_name || r.data.project_name,
          project_manager: f.project_manager || r.data.project_manager,
        }));
        toast.success('Matched project — customer/PM filled');
      }
    } catch { /* silent */ }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await axios.post(`${API}/projects`, form);
      else await axios.put(`${API}/projects/${project.id}`, form);
      toast.success(isNew ? 'Project created' : 'Project saved');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  const est = parseFloat(form.estimated_hours) || 0;
  const nAssigned = (form.assigned_employee_ids || []).length || 1;
  const estDays = est > 0 ? Math.ceil(est / (nAssigned * 8)) : 0;

  const Field = ModalField;
  const inputCls = MODAL_INPUT_CLS;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-6" onClick={e => e.stopPropagation()} data-testid="fab-project-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">{isNew ? 'New Fabrication Project' : `Edit ${form.work_order_number || form.project_name || 'Project'}`}</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500 hover:text-slate-700" /></button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Work Order #">
            <input value={form.work_order_number} onChange={e => set('work_order_number', e.target.value)} onBlur={woLookup} data-testid="fab-f-wo" className={inputCls} placeholder="26-XXXX" />
          </Field>
          <Field label="Customer"><input value={form.customer_name} onChange={e => set('customer_name', e.target.value)} data-testid="fab-f-customer" className={inputCls} /></Field>
          <Field label="Project Manager"><input value={form.project_manager} onChange={e => set('project_manager', e.target.value)} className={inputCls} /></Field>
          <Field label="Project Name"><input value={form.project_name} onChange={e => set('project_name', e.target.value)} data-testid="fab-f-name" className={inputCls} /></Field>
          <Field label="Priority">
            <select value={form.priority} onChange={e => set('priority', e.target.value)} data-testid="fab-f-priority" className={inputCls}>
              {(meta?.priorities || []).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={e => set('status', e.target.value)} data-testid="fab-f-status" className={inputCls}>
              {(meta?.statuses || []).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="Date Received"><DatePickerField value={form.date_received} onChange={v => set('date_received', v)} className={inputCls} testid="fab-f-received" /></Field>
          <Field label="Required Completion"><DatePickerField value={form.required_completion_date} onChange={v => set('required_completion_date', v)} className={inputCls} testid="fab-f-required" /></Field>
          <Field label="Work Area">
            <select value={form.work_area || ''} onChange={e => set('work_area', e.target.value)} className={inputCls}>
              <option value="">—</option>
              {(meta?.work_areas || []).map(w => <option key={w} value={w}>{w}</option>)}
            </select>
          </Field>
          {!isNew && (project.worked_by?.length || project.auto_added) && (
            <div className="md:col-span-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="fab-shop-activity">
              <b>From shop timesheets:</b> {project.shop_hours ?? 0}h logged{project.first_worked_date ? ` · ${fmtShort(project.first_worked_date)} → ${fmtShort(project.last_worked_date)}` : ''}
              {project.worked_by?.length ? <> · {project.worked_by.map(w => `${w.name} (${w.hours}h)`).join(', ')}</> : null}
              {project.auto_added && <span className="ml-2 font-semibold">· card auto-added{project.status_source === 'shop_timesheets' ? ' — set the estimate & dates' : ''}</span>}
              {project.status_source === 'manual' && <span className="ml-2 text-emerald-700">· status set by hand (sync won't change it)</span>}
            </div>
          )}
          <Field label="Estimated Hours"><input type="number" value={form.estimated_hours ?? ''} onChange={e => set('estimated_hours', e.target.value)} data-testid="fab-f-est" className={inputCls} /></Field>
          <Field label="Actual Hours (override)"><input type="number" value={form.actual_hours_override ?? ''} onChange={e => set('actual_hours_override', e.target.value)} placeholder={isNew ? '' : `auto: ${project.actual_hours ?? 0}`} className={inputCls} /></Field>
          <Field label="% Complete (override)"><input type="number" value={form.percent_complete_override ?? ''} onChange={e => set('percent_complete_override', e.target.value)} placeholder={isNew ? '' : `auto: ${project.percent_complete ?? 0}%`} className={inputCls} /></Field>
          <Field label="Planned Start"><DatePickerField value={form.planned_start_date} onChange={v => set('planned_start_date', v)} className={inputCls} testid="fab-f-start" /></Field>
          <Field label="Planned Completion"><DatePickerField value={form.planned_completion_date} onChange={v => set('planned_completion_date', v)} min={form.planned_start_date || undefined} className={inputCls} testid="fab-f-comp" /></Field>
          <Field label="Sequence #"><input type="number" value={form.sequence_number ?? ''} onChange={e => set('sequence_number', e.target.value)} className={inputCls} /></Field>
          <Field label="Material Status">
            <select value={form.material_status} onChange={e => set('material_status', e.target.value)} data-testid="fab-f-material" className={inputCls}>
              <option value="not_ready">Not ready</option><option value="ordered">Ordered</option><option value="ready">Ready</option>
            </select>
          </Field>
          <Field label="Drawing Status">
            <select value={form.drawing_status} onChange={e => set('drawing_status', e.target.value)} data-testid="fab-f-drawing" className={inputCls}>
              <option value="not_ready">Not ready</option><option value="in_progress">In progress</option><option value="ready">Ready</option>
            </select>
          </Field>
          <Field label="Subcontract Status">
            <select value={form.subcontract_status} onChange={e => set('subcontract_status', e.target.value)} className={inputCls}>
              <option value="none">None</option><option value="out">Out</option><option value="received">Received</option>
            </select>
          </Field>

          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">Assigned Employees {estDays > 0 && <span className="text-slate-500">· est. {estDays} working day{estDays !== 1 ? 's' : ''} with {nAssigned} assigned</span>}</label>
            <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto border border-slate-200 rounded p-2">
              {employees.length === 0 && <span className="text-xs text-slate-500">No fab employees found</span>}
              {employees.map(e => {
                const on = (form.assigned_employee_ids || []).includes(e.employee_id);
                return (
                  <button key={e.employee_id} type="button" onClick={() => toggleEmp(e.employee_id)}
                    data-testid={`fab-emp-${e.employee_id}`}
                    className={`px-2 py-1 rounded text-xs border ${on ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`}>
                    {e.name} <span className="opacity-70">({e.trade})</span>
                  </button>
                );
              })}
            </div>
          </div>

          {!isNew && (
            <Field label="Reason for date change (if any)">
              <select value={form.change_reason} onChange={e => set('change_reason', e.target.value)} className={inputCls}>
                <option value="">—</option>
                {(meta?.change_reasons || []).map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          )}
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes / scheduling concerns</label>
            <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={2} className={inputCls} />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="fab-save-project" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gantt / Weekly Schedule ──────────────────────────────────────────
function GanttTab({ winStart, winEnd, meta }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/projects`).then(r => setProjects(r.data.projects || [])).finally(() => setLoading(false));
  }, [winStart]);

  const days = [];
  { const s = parseD(winStart), e = parseD(winEnd); const c = new Date(s); while (c <= e) { days.push(new Date(c)); c.setDate(c.getDate() + 1); } }
  const todayStr = localDate(new Date());

  const inWindow = projects.filter(p => p.planned_start_date && p.planned_completion_date);

  return (
    <div className="bg-white rounded-xl shadow-lg p-4">
      <div className="flex flex-wrap gap-3 mb-3 text-xs">
        {Object.entries({ emergency: 'Emergency', rush: 'Rush', high: 'High', standard: 'Standard', low: 'Low' }).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1"><span className={`w-3 h-3 rounded ${PRIORITY_BAR[k]}`} /> {v}</span>
        ))}
      </div>
      {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> :
        inWindow.length === 0 ? <div className="p-8 text-center text-slate-500" data-testid="fab-gantt-empty">No scheduled projects (set planned start & completion dates on a project to see it here).</div> : (
          <div className="overflow-x-auto">
            <div style={{ minWidth: 220 + days.length * 34 }}>
              <div className="flex border-b border-slate-200 sticky top-0 bg-white">
                <div className="w-[220px] shrink-0 px-2 py-1 text-xs font-semibold text-slate-500">Project</div>
                {days.map((d, i) => {
                  const ds = localDate(d); const we = d.getDay() === 0 || d.getDay() === 6;
                  return <div key={i} className={`w-[34px] shrink-0 text-center text-[10px] py-1 ${we ? 'bg-slate-100 text-slate-500' : 'text-slate-500'} ${ds === todayStr ? 'bg-blue-100 font-bold text-blue-700' : ''}`}>
                    <div>{d.toLocaleDateString('en-US', { weekday: 'narrow' })}</div><div>{d.getDate()}</div>
                  </div>;
                })}
              </div>
              {inWindow.map(p => {
                const ps = parseD(p.planned_start_date), pc = parseD(p.planned_completion_date), req = parseD(p.required_completion_date);
                return (
                  <div key={p.id} className="flex border-b border-slate-100 items-center hover:bg-slate-50" data-testid={`fab-gantt-row-${p.id}`}>
                    <div className="w-[220px] shrink-0 px-2 py-1.5 overflow-hidden">
                      <div className="flex items-center gap-1">
                        <span className="font-mono text-xs font-bold text-blue-700 truncate">{p.work_order_number || p.project_name}</span>
                        {(p.warnings || []).length > 0 && <AlertTriangle className="w-3 h-3 text-amber-600 shrink-0" title={p.warnings.join('\n')} />}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">{p.customer_name} · {p.remaining_hours}h left</div>
                    </div>
                    {days.map((d, i) => {
                      const inBar = ps && pc && d >= ps && d <= pc;
                      const ds = localDate(d);
                      const isReq = req && ds === localDate(req);
                      return (
                        <div key={i} className={`w-[34px] shrink-0 h-9 flex items-center justify-center ${ds === todayStr ? 'bg-blue-50' : ''} ${isReq ? 'border-l-2 border-red-500' : ''}`}>
                          {inBar && <div className={`w-full h-4 ${PRIORITY_BAR[p.priority] || 'bg-blue-400'} ${d.getTime() === ps.getTime() ? 'rounded-l' : ''} ${d.getTime() === pc.getTime() ? 'rounded-r' : ''}`} />}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      <p className="text-[11px] text-slate-500 mt-2">Red line = required completion date · Blue column = today</p>
    </div>
  );
}

// ── Employee Schedule ────────────────────────────────────────────────
function EmployeeTab({ winStart, winEnd, canEdit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCap, setShowCap] = useState(false);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/employees`, { params: { date_from: winStart, date_to: winEnd } })
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, [winStart, winEnd]);
  useEffect(() => { load(); }, [load]);

  const dates = data?.dates || [];
  return (
    <div className="bg-white rounded-xl shadow-lg p-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-slate-600">Assigned load per working day (red = overbooked)</h3>
        {canEdit && <button onClick={() => setShowCap(true)} data-testid="fab-manage-capacity" className="text-sm px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700">Manage Capacity</button>}
      </div>
      {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> : (
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="px-2 py-1 text-left sticky left-0 bg-white">Employee</th>
                {dates.map(d => <th key={d} className="px-1 py-1 text-center text-slate-500 w-[42px]">{fmtShort(d)}</th>)}
              </tr>
            </thead>
            <tbody>
              {(data?.employees || []).map(emp => (
                <tr key={emp.employee_id} className="border-t border-slate-100" data-testid={`fab-emp-row-${emp.employee_id}`}>
                  <td className="px-2 py-1.5 sticky left-0 bg-white">
                    <div className="font-medium text-slate-800 whitespace-nowrap">{emp.name} {emp.overbooked && <AlertTriangle className="w-3 h-3 text-red-500 inline" />}</div>
                    <div className="text-[10px] text-slate-500">{emp.trade} · {emp.available_hours_per_day}h/day</div>
                  </td>
                  {emp.days.map(d => (
                    <td key={d.date} className="px-1 py-1.5 text-center">
                      <div className={`rounded text-[10px] py-0.5 ${d.scheduled === 0 ? 'text-slate-700' : d.overbooked > 0 ? 'bg-red-100 text-red-700 font-bold' : 'bg-emerald-50 text-emerald-700'}`}>
                        {d.scheduled === 0 ? '·' : d.scheduled}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showCap && <CapacityEditor onClose={() => { setShowCap(false); load(); }} />}
    </div>
  );
}

function CapacityEditor({ onClose }) {
  const [emps, setEmps] = useState([]);
  useEffect(() => { axios.get(`${API}/employees`).then(r => setEmps(r.data.employees || [])); }, []);
  const save = async (emp, patch) => {
    try {
      await axios.put(`${API}/employees/${emp.employee_id}/capacity`, {
        available_hours_per_day: emp.available_hours_per_day,
        trade: emp.trade,
        vacation_dates: emp.vacation_dates || [],
        ...patch,
      });
      toast.success('Saved');
    } catch { toast.error('Save failed'); }
  };
  const upd = (id, k, v) => setEmps(es => es.map(e => e.employee_id === id ? { ...e, [k]: v } : e));
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-6" onClick={e => e.stopPropagation()} data-testid="fab-capacity-editor">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-bold text-slate-900">Employee Capacity</h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="p-4 space-y-2 max-h-[70vh] overflow-y-auto">
          {emps.map(e => (
            <div key={e.employee_id} className="flex items-center gap-2 border-b border-slate-100 pb-2">
              <div className="flex-1"><div className="text-sm font-medium text-slate-800">{e.name}</div><div className="text-[10px] text-slate-500">{e.role}</div></div>
              <div>
                <label className="block text-[10px] text-slate-500">Hrs/day</label>
                <input type="number" value={e.available_hours_per_day} data-testid={`fab-cap-hours-${e.employee_id}`}
                  onChange={ev => upd(e.employee_id, 'available_hours_per_day', parseFloat(ev.target.value) || 0)}
                  className="w-16 px-2 py-1 border border-slate-300 rounded text-sm text-slate-900" />
              </div>
              <div className="w-56">
                <label className="block text-[10px] text-slate-500">Vacation dates (comma separated YYYY-MM-DD)</label>
                <input value={(e.vacation_dates || []).join(', ')}
                  onChange={ev => upd(e.employee_id, 'vacation_dates', ev.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                  className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" />
              </div>
              <button onClick={() => save(e)} data-testid={`fab-cap-save-${e.employee_id}`} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium self-end">Save</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Capacity view ────────────────────────────────────────────────────
function CapacityTab({ winStart, winEnd }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/capacity`, { params: { date_from: winStart, date_to: winEnd } })
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, [winStart, winEnd]);

  const maxVal = Math.max(1, ...((data?.days || []).map(d => Math.max(d.available, d.scheduled))));
  return (
    <div className="bg-white rounded-xl shadow-lg p-4">
      <p className="text-sm text-slate-600 mb-4">{data?.employee_count || 0} fab employees · shop hours available vs scheduled per working day</p>
      {loading ? <div className="p-8 text-center text-slate-500">Loading…</div> : (
        <div className="space-y-2">
          {(data?.days || []).map(d => (
            <div key={d.date} className="flex items-center gap-3" data-testid={`fab-cap-day-${d.date}`}>
              <div className="w-16 text-xs text-slate-600 shrink-0">{fmtShort(d.date)}</div>
              <div className="flex-1 relative h-6 bg-slate-100 rounded overflow-hidden">
                <div className="absolute top-0 left-0 h-full bg-blue-200" style={{ width: `${(d.available / maxVal) * 100}%` }} />
                <div className={`absolute top-0 left-0 h-full ${d.overbooked > 0 ? 'bg-red-400' : 'bg-emerald-400'}`} style={{ width: `${(d.scheduled / maxVal) * 100}%` }} />
              </div>
              <div className="w-40 text-xs text-slate-600 shrink-0 text-right">
                {d.scheduled}/{d.available}h {d.overbooked > 0 ? <span className="text-red-600 font-semibold">· over {d.overbooked}h</span> : <span className="text-emerald-600">· {d.remaining}h free</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-4 mt-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-200 rounded" /> Available</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-400 rounded" /> Scheduled</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded" /> Overbooked</span>
      </div>
    </div>
  );
}
